import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Save, Check, Plus, X, Users, ShieldCheck } from 'lucide-react';
import { adminGetEmailAllowlist, adminSetEmailAllowlist, adminListUsers, type AllowlistMode, type AdminUserRow } from '../../services/adminService';

/**
 * Admin recipient-allowlist control — the staged-rollout gate for WHO receives the weekly broadcast. Mode 'all'
 * sends to every eligible user (normal); mode 'allowlist' narrows to a specific set (start with a test mailbox,
 * add real users as confidence grows). Pref/suppression still apply on top — this only NARROWS the audience.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EmailAllowlistPanel: React.FC = () => {
  const [mode, setMode] = useState<AllowlistMode>('all');
  const [emails, setEmails] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  // Autocomplete: type a partial email → look up matching users so the admin needn't remember the full address.
  const [suggestions, setSuggestions] = useState<AdminUserRow[]>([]);
  const [lookingUp, setLookingUp] = useState(false);
  const [showSuggest, setShowSuggest] = useState(false);

  useEffect(() => {
    adminGetEmailAllowlist()
      .then((c) => { setMode(c.mode); setEmails(c.emails || []); })
      .catch(() => setError('Could not load the allowlist'))
      .finally(() => setLoading(false));
  }, []);

  // Debounced user lookup on the draft input (email PREFIX match, server-side). Skips when nothing typed.
  const draftRef = useRef(draft);
  draftRef.current = draft;
  useEffect(() => {
    const q = draft.trim().toLowerCase();
    if (mode !== 'allowlist' || q.length < 2) { setSuggestions([]); setLookingUp(false); return; }
    setLookingUp(true);
    const id = setTimeout(() => {
      adminListUsers(q, 6)
        .then((r) => { if (draftRef.current.trim().toLowerCase() === q) setSuggestions(r.users || []); })
        .catch(() => setSuggestions([]))
        .finally(() => { if (draftRef.current.trim().toLowerCase() === q) setLookingUp(false); });
    }, 250);
    return () => clearTimeout(id);
  }, [draft, mode]);

  const addValue = (raw: string) => {
    const e = raw.trim().toLowerCase();
    if (!EMAIL_RE.test(e)) { setError('Enter a valid email address'); return; }
    if (!emails.includes(e)) setEmails((p) => [...p, e]);
    setDraft(''); setError(''); setSuggestions([]); setShowSuggest(false);
  };
  const addEmail = () => addValue(draft);
  const removeEmail = (e: string) => setEmails((p) => p.filter((x) => x !== e));
  // Suggestions worth showing: have an email, not already added.
  const freshSuggestions = suggestions.filter((u) => u.email && !emails.includes(u.email.toLowerCase()));

  const save = async () => {
    setSaving(true); setSaved(false); setError('');
    try {
      const c = await adminSetEmailAllowlist({ mode, emails });
      setMode(c.mode); setEmails(c.emails || []);
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch { setError('Save failed — try again'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="rounded-2xl border border-secondary-200 dark:border-secondary-800 p-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-secondary-400" /></div>;

  return (
    <div className="rounded-2xl border border-secondary-200 dark:border-secondary-800 p-5">
      <div className="mb-1 flex items-center justify-between">
        <h2 className="font-semibold">Who receives emails <span className="text-secondary-400 font-normal text-sm">· weekly broadcast</span></h2>
        <button onClick={save} disabled={saving}
          className="inline-flex items-center gap-1.5 rounded-lg bg-secondary-900 dark:bg-white px-3 py-1.5 text-xs font-semibold text-white dark:text-secondary-900 hover:bg-black dark:hover:bg-secondary-100 disabled:opacity-40">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : saved ? <Check className="w-3.5 h-3.5" /> : <Save className="w-3.5 h-3.5" />} {saved ? 'Saved' : 'Save'}
        </button>
      </div>
      <p className="text-xs text-secondary-500 mb-4">Narrows who gets the weekly summary. Unsubscribe + suppression always apply on top.</p>

      {/* Mode toggle */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        {([['all', 'All eligible users', Users], ['allowlist', 'Allowlist only', ShieldCheck]] as const).map(([m, label, Icon]) => (
          <button key={m} onClick={() => setMode(m)}
            className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm text-left transition ${mode === m
              ? 'border-secondary-900 dark:border-white bg-secondary-50 dark:bg-secondary-800 font-semibold'
              : 'border-secondary-200 dark:border-secondary-700 text-secondary-500 hover:border-secondary-300'}`}>
            <Icon className="w-4 h-4 shrink-0" /> <span className="min-w-0 truncate">{label}</span>
          </button>
        ))}
      </div>

      {/* Email list — only meaningful in allowlist mode */}
      <div className={mode === 'allowlist' ? '' : 'opacity-40 pointer-events-none'}>
        <div className="flex gap-2">
          <div className="relative flex-1 min-w-0">
            <input value={draft}
              onChange={(e) => { setDraft(e.target.value); setShowSuggest(true); }}
              onFocus={() => setShowSuggest(true)}
              onBlur={() => setTimeout(() => setShowSuggest(false), 150)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } if (e.key === 'Escape') setShowSuggest(false); }}
              placeholder="Start typing a name or email…" inputMode="email" autoComplete="off"
              className="w-full rounded-lg border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-900 px-3 py-2 text-sm text-secondary-900 dark:text-white focus:outline-none focus:border-secondary-500" />
            {showSuggest && draft.trim().length >= 2 && (
              <div className="absolute z-20 mt-1 w-full rounded-lg border border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-900 shadow-lg overflow-hidden">
                {lookingUp && !freshSuggestions.length ? (
                  <div className="flex items-center gap-2 px-3 py-2.5 text-xs text-secondary-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Looking up users…</div>
                ) : freshSuggestions.length === 0 ? (
                  <div className="px-3 py-2.5 text-xs text-secondary-400">No matching users — press Add to whitelist this address anyway.</div>
                ) : (
                  freshSuggestions.map((u) => (
                    <button key={u.uid} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => addValue(u.email as string)}
                      className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-secondary-50 dark:hover:bg-secondary-800">
                      <span className="min-w-0">
                        <span className="block truncate text-sm text-secondary-900 dark:text-white">{u.email}</span>
                        {u.name && <span className="block truncate text-xs text-secondary-400">{u.name}</span>}
                      </span>
                      <Plus className="w-3.5 h-3.5 shrink-0 text-secondary-400" />
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <button onClick={addEmail}
            className="inline-flex items-center gap-1.5 rounded-lg border border-secondary-300 dark:border-secondary-700 px-3 py-2 text-xs font-semibold text-secondary-700 dark:text-secondary-200 hover:bg-secondary-50 dark:hover:bg-secondary-800">
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {emails.length === 0
            ? <p className="text-xs text-secondary-400">No addresses yet — add one above to start the rollout.</p>
            : emails.map((e) => (
              <span key={e} className="inline-flex items-center gap-1.5 rounded-full border border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-800 pl-3 pr-1.5 py-1 text-xs text-secondary-700 dark:text-secondary-200">
                <span className="max-w-[220px] truncate">{e}</span>
                <button onClick={() => removeEmail(e)} aria-label={`Remove ${e}`} className="rounded-full p-0.5 hover:bg-secondary-200 dark:hover:bg-secondary-700"><X className="w-3 h-3" /></button>
              </span>
            ))}
        </div>
      </div>

      {error && <p className="mt-3 text-xs text-red-500">{error}</p>}
    </div>
  );
};

export default EmailAllowlistPanel;
